import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { apiClient } from '../api';
import { useAuth } from '../context/AuthContext';

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) {
      toast.error('Password too short', { description: 'At least 6 characters.' });
      return;
    }
    if (pwd !== pwd2) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.post('/auth/reset-password', { token, new_password: pwd });
      toast.success('Password reset! Logging you in...');
      await refresh();
      navigate(res.data?.user?.has_profile ? '/browse' : '/profile/setup');
    } catch (err) {
      toast.error('Reset failed', { description: err.response?.data?.detail || '' });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-fnl-dark flex items-center justify-center px-4">
        <div className="bg-fnl-purple/80 rounded-2xl p-8 max-w-md text-center" data-testid="reset-no-token">
          <h2 className="text-xl font-bold text-white mb-2">Invalid link</h2>
          <p className="text-purple-300 text-sm mb-4">This reset link is missing or malformed.</p>
          <Link to="/forgot-password" className="text-fnl-pink hover:underline font-semibold">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fnl-dark flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="relative">
            <span className="text-white font-bold text-3xl">F</span>
            <Heart className="absolute -top-1 -right-3 w-4 h-4 fill-fnl-pink text-fnl-pink" />
          </div>
          <span className="text-white font-semibold text-lg">Flirty</span>
        </Link>
        <div className="bg-fnl-purple/80 backdrop-blur rounded-2xl p-8 border border-purple-900/50">
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Choose a new password</h2>
          <p className="text-purple-300 text-sm text-center mb-6">Pick something only you would guess.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-white text-sm mb-2 block">New password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-300" />
                <Input
                  type="password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="pl-10 bg-white/95 border-0 h-12"
                  data-testid="reset-pwd"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-white text-sm mb-2 block">Confirm password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-300" />
                <Input
                  type="password"
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  className="pl-10 bg-white/95 border-0 h-12"
                  data-testid="reset-pwd2"
                  required
                />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 bg-fnl-pink hover:bg-fnl-pink text-white font-semibold btn-shadow-pink" data-testid="reset-submit">
              {loading ? 'Resetting...' : 'Reset password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
